import { Aspect } from './aspect';
import { Point, Region } from './region';

enum TranslationPinMode
{
  LongSide,
  ShortSide,
}

enum OriginShiftMode
{
  X,
  Y,
}

class OriginShifter
{
  private origin: Point;

  constructor(
      orientation: Aspect.Orientation,
      pinMode: TranslationPinMode,
      containerFreeSide: number,
      wrappedFreeSide: number,
      viewportCenter?: Point)
  {
    let offset = (containerFreeSide - wrappedFreeSide) / 2;
    if (viewportCenter) {
      const center = orientation === Aspect.Orientation.Landscape ?
          viewportCenter.x : viewportCenter.y;
      offset = center - (wrappedFreeSide / 2);
      if (offset < 0) {
        offset = 0;
      }
      if (offset > containerFreeSide - wrappedFreeSide) {
        offset = containerFreeSide - wrappedFreeSide;
      }
    }

    const shiftMode = (() => {
      if (orientation === Aspect.Orientation.Landscape) {
        return pinMode === TranslationPinMode.LongSide
            ? OriginShiftMode.Y
            : OriginShiftMode.X;
      } else {
        return pinMode === TranslationPinMode.LongSide
            ? OriginShiftMode.X
            : OriginShiftMode.Y;
      }
    })();

    this.origin =
        shiftMode === OriginShiftMode.X
            ? new Point(offset, 0)
            : new Point(0, offset);
  }

  public addShift(point: Point): Point
  {
    return new Point(point.x + this.origin.x, point.y + this.origin.y);
  }

  public removeShift(point: Point): Point
  {
    return new Point(point.x - this.origin.x, point.y - this.origin.y);
  }
}

export class CoordinateTranslation
{
  private origin: OriginShifter;
  private orientation: Aspect.Orientation;
  private translationFactor: number;
  private target: Region;

  public readonly legalRegion: Region;

  constructor(target: Region, viewport: Region, viewportCenter?: Point)
  {
    this.target = target;
    this.orientation = Aspect.getOrientation(viewport);

    const pinMode =
        Aspect.getAspectRatio(this.orientation, viewport)
            < Aspect.getAspectRatio(this.orientation, target)
            ? TranslationPinMode.LongSide
            : TranslationPinMode.ShortSide;

    const pinnedSide =
        pinMode === TranslationPinMode.LongSide
            ? Aspect.getLongSide
            : Aspect.getShortSide;

    const freeSide =
        pinMode === TranslationPinMode.LongSide
            ? Aspect.getShortSide
            : Aspect.getLongSide;

    this.translationFactor =
        pinnedSide(this.orientation, target)
        / pinnedSide(this.orientation, viewport);

    this.origin = new OriginShifter(
        this.orientation,
        pinMode,
        freeSide(this.orientation, viewport),
        this.coordToViewportSpace(freeSide(this.orientation, target)),
        viewportCenter);

    this.legalRegion = this.regionToViewportSpace(this.target);
  }

  private coordToTargetSpace(viewportCoord: number): number
  {
    return viewportCoord * this.translationFactor;
  }

  private coordToViewportSpace(targetCoord: number): number
  {
    return targetCoord / this.translationFactor;
  }

  public scalePointToTargetSpace(viewportPoint: Point): Point
  {
    return new Point(
        this.coordToTargetSpace(viewportPoint.x),
        this.coordToTargetSpace(viewportPoint.y));
  }

  public scalePointToViewportSpace(targetPoint: Point): Point
  {
    return new Point(
        this.coordToViewportSpace(targetPoint.x),
        this.coordToViewportSpace(targetPoint.y));
  }

  public pointToTargetSpace(viewportPoint: Point): Point
  {
    return this.scalePointToTargetSpace(this.origin.removeShift(viewportPoint));
  }

  public pointToViewportSpace(targetPoint: Point): Point
  {
    return this.origin.addShift(this.scalePointToViewportSpace(targetPoint));
  }

  public regionToTargetSpace(viewportRegion: Region): Region
  {
    return Region.fromTwoPoints(
        this.pointToTargetSpace(viewportRegion.topLeft),
        this.pointToTargetSpace(viewportRegion.bottomRight));
  }

  public regionToViewportSpace(targetRegion: Region): Region
  {
    return Region.fromTwoPoints(
        this.pointToViewportSpace(targetRegion.topLeft),
        this.pointToViewportSpace(targetRegion.bottomRight));
  }
}



// WEBPACK FOOTER //
// ./src/app/controls/image-cropper/coordinate-translation.ts